/**
 * Inventory-related types shared between backend and frontend
 */

/**
 * Lifecycle status of a metadata change set (a batch of staged metadata edits
 * reviewed/exported as a unit).
 */
export enum MetadataChangeSetStatus {
  Draft = "draft",
  Published = "published",
}

/**
 * Change-set-level event history actions: what happened to a metadata change
 * set (not to an individual staged row) — creation, detail edits, status and
 * assignee changes, staged-edit saves, and version restores.
 */
export enum MetadataChangeSetEventAction {
  Created = "created",
  DetailsUpdated = "details_updated",
  StatusChanged = "status_changed",
  AssigneeChanged = "assignee_changed",
  EditsStaged = "edits_staged",
  VersionRestored = "version_restored",
  NewRowAdded = "new_row_added",
  NewRowRemoved = "new_row_removed",
}

/**
 * What produced one version of a change set's staged edits: the change set's
 * creation, someone saving edits to its rows, or someone adding/removing an
 * authored new row.
 */
export enum MetadataChangeSetVersionSource {
  Created = "created",
  EditsSaved = "edits_saved",
  NewRowAdded = "new_row_added",
  NewRowRemoved = "new_row_removed",
}

/**
 * Pricelist-level event history actions: what happened to one uploaded
 * pricelist on its details page — company assignment, extraction re-runs and
 * CSV edits, version restores, deletion, and each of the manually maintained
 * detail fields.
 */
export enum PricelistEventAction {
  Assigned = "assigned",
  Deleted = "deleted",
  ExtractionRetried = "extraction_retried",
  CsvEdited = "csv_edited",
  DivisionPriceChangesUpdated = "division_price_changes_updated",
  TariffChangeFlagChanged = "tariff_change_flag_changed",
  AnticipatedChangeUpdated = "anticipated_change_updated",
  NotesUpdated = "notes_updated",
  VersionRestored = "version_restored",
}

/**
 * What produced one version of a pricelist's extracted tables: the original
 * upload, a re-run of the extraction, or someone saving edits to the tables.
 */
export enum PricelistVersionSource {
  Upload = "upload",
  Extraction = "extraction",
  Edit = "edit",
}

/**
 * Status of inventory availability check
 */
export enum InventoryStatus {
  /** Available quantity meets or exceeds requested quantity */
  SUFFICIENT = "SUFFICIENT",
  /** Available quantity is less than requested quantity */
  INSUFFICIENT = "INSUFFICIENT",
  /** Unable to determine inventory status (e.g., no connection, item not found) */
  UNKNOWN = "UNKNOWN",
  /** Item is marked as inactive and cannot be fulfilled */
  INACTIVE = "INACTIVE",
}

/**
 * Result of an inventory validation check for a line item
 */
export interface InventoryValidationResult {
  /** Status of the inventory check */
  status: InventoryStatus;
  /** Quantity requested on the line item */
  requestedQuantity: number;
  /** Available quantity (on-hand minus reserved), null if unknown */
  availableQuantity: number | null;
  /** Total on-hand quantity, null if unknown */
  onhandQuantity: number | null;
  /** Reserved quantity, null if unknown */
  reservedQuantity: number | null;
  /** Organization code used for the query */
  organizationCode: string | null;
  /** Item number/SKU used for the query */
  itemNumber: string | null;
  /** Error message if the check failed */
  errorMessage?: string;
  /** Raw item status from the ERP system (e.g. "Active", "Inactive") */
  itemStatus?: string;
}
